export interface SupBulkLookupOption {
  id: string | number;
  name: string;
}

export interface SupBulkEntitySelections {
  countryId?: string | null;
  stateId?: string | null;
  cityId?: string | null;
  pincodeId?: string | null;
  countryName?: string | null;
  stateName?: string | null;
  cityName?: string | null;
  pincode?: string | null;
  licenseImage?: string | null;
}

export interface SupBulkSkuDraft {
  name: string;
  netWeight: string;
  mrp: string;
  manufacturedBy: string;
  marketedBy: string;
  packagedBy: string;
  unitId?: string | null;
  unitName?: string | null;
  countryId?: string | null;
  countryName?: string | null;
  productImage?: string | null;
  productBarcode?: string | null;
}

export interface SupBulkIngredientDraft {
  name: string;
  amount: string;
  unit: string;
}

export interface SupBulkResolvedIngredient {
  ingredientId: string;
  name: string;
  servingUnitMasterId: string;
  unit: string;
  amount: string;
  insNumber?: string;
  category?: string;
  isNew?: boolean;
}

export interface SupBulkNutrientItem {
  nutrientId: string;
  nutrientName: string;
  unitId: string;
  unitName: string;
  amount: string | number;
  percentDV?: string | number;
  rda?: string | number;
}

export interface SupBulkClaimDraft {
  claimTypeId: string | number | null;
  claimTypeName: string;
  claimText: string;
  selectedTagIds: Array<string | number>;
  selectedTagNames: string[];
  isDone?: boolean;
  /** Stable list for ng-select — avoid rebuilding items on every CD cycle. */
  availableTags?: SupBulkLookupOption[];
}

export interface SupBulkHealthConditionGroup {
  groupId: string;
  groupName: string;
  tags: Array<{ id: string; name: string }>;
}

export interface SupBulkNutrientTagOption extends SupBulkLookupOption {
  groupId?: string | number | null;
}

export interface SupBulkClaimTagOption extends SupBulkLookupOption {
  claimTypeId?: string | number | null;
  productBenefitId?: string | number | null;
}

export interface SupBulkAssignment {
  mode: 'maker-and-checker' | 'checker-only';
  makerEmail?: string | null;
  checkerEmail?: string | null;
  dueDate?: string | null;
  priority?: string;
  notes?: string | null;
}

export interface SupBulkImportItem {
  payload: Record<string, unknown>;
  assignment?: SupBulkAssignment | null;
}

export interface SupBulkRowSelections {
  status: string;
  countryId?: string | null;
  stateId?: string | null;
  cityId?: string | null;
  pincodeId?: string | null;
  countryName?: string | null;
  stateName?: string | null;
  cityName?: string | null;
  pincode?: string | null;
  dietaryPreferenceId?: string | number | null;
  /** Supplement product form master id (maps to productForm / supplementFormId). */
  productForm?: string | null;
  dataSourceId?: string | number | null;
  databaseSourceId?: string | number | null;
  countryOfOriginId?: string | number | null;
  shelfLifeUnit?: string | null;
  netQuantityUnit?: string | null;
  frontImage?: string | null;
  backImage?: string | null;
  leftImage?: string | null;
  rightImage?: string | null;
  frontImageUploading?: boolean;
  authorizedCertificateImage?: string | null;
  authorizedCertificateUploading?: boolean;
  authorizedLicenseImage?: string | null;
  authorizedLicenseUploading?: boolean;
  manufacturedBy: SupBulkEntitySelections;
  marketedBy: SupBulkEntitySelections;
  packagedBy: SupBulkEntitySelections;
  fssaiCategoryId?: string | null;
  fssaiSubCategoryId?: string | null;
  fssaiSubSubCategoryId?: string | null;
  eStoreCategoryId?: string | null;
  eStoreSubCategoryId?: string | null;
  eStoreSubSubCategoryId?: string | null;
  fssaiCategoryName?: string | null;
  fssaiSubCategoryName?: string | null;
  fssaiSubSubCategoryName?: string | null;
  eStoreCategoryName?: string | null;
  eStoreSubCategoryName?: string | null;
  eStoreSubSubCategoryName?: string | null;
  tasteIds: Array<string | number>;
  flavourIds: Array<string | number>;
  ingredientDrafts: SupBulkIngredientDraft[];
  ingredients: SupBulkResolvedIngredient[];
  recommendedDosageAmount?: string | number | null;
  recommendedDosageUnitId?: string | number | null;
  recommendedDosageNote?: string | null;
  macronutrients: SupBulkNutrientItem[];
  micronutrients: SupBulkNutrientItem[];
  allergenDeclaredIds: Array<string | number>;
  mayContainIds: Array<string | number>;
  producedInFacilityIds: Array<string | number>;
  allergenContainsIds: Array<string | number>;
  allergenFreeIds: Array<string | number>;
  intoleranceContainsIds: Array<string | number>;
  intoleranceFreeIds: Array<string | number>;
  productClaims: SupBulkClaimDraft[];
  productBenefitClaims: SupBulkClaimDraft[];
  ingredientClaims: SupBulkClaimDraft[];
  healthConditionTags: SupBulkHealthConditionGroup[];
  generatedFssaiNutrientTags: Array<{ fssaiTagName: string; fssaiNutrientTagMasterId?: number }>;
  skuDetails: SupBulkSkuDraft[];
  assignment?: SupBulkAssignment | null;
}

export interface SupBulkParsedText {
  companyName: string;
  address: string;
  executiveOwner: string;
  mobileNumber: string;
  email: string;
  gstStatus: string;
  brandName: string;
  productName: string;
  brandWebsite: string;
  instagram: string;
  facebook: string;
  linkedin: string;
  shelfLife: string;
  netQuantity: string;
  lotNumber: string;
  referenceUrl: string;
  availableFlavour: string;
  supplementDescription: string;
  dataCapturingDate: string;
  dateOfManufacturing: string;
  dateOfExpiry: string;
  bestBeforeDate: string;
  manufacturedByName: string;
  manufacturedByAddress: string;
  manufacturedByLicenseNo: string;
  marketedByName: string;
  marketedByAddress: string;
  marketedByLicenseNo: string;
  packagedByName: string;
  packagedByAddress: string;
  packagedByLicenseNo: string;
  country: string;
  state: string;
  city: string;
  pincode: string;
  dietaryPreference: string;
  productForm: string;
  dataSource: string;
  databaseSource: string;
  countryOfOrigin: string;
  shelfLifeUnit: string;
  netQuantityUnit: string;
  fssaiCategory: string;
  eStoreCategory: string;
  tastes: string;
  flavours: string;
  manufacturedByCountry: string;
  manufacturedByState: string;
  manufacturedByCity: string;
  manufacturedByPincode: string;
  marketedByCountry: string;
  marketedByState: string;
  marketedByCity: string;
  marketedByPincode: string;
  packagedByCountry: string;
  packagedByState: string;
  packagedByCity: string;
  packagedByPincode: string;
  skuRaw: string;
  ingredientsRaw: string;
  recommendedDosageAmount: string;
  recommendedDosageUnit: string;
  recommendedDosageNote: string;
  macrosRaw: string;
  microsRaw: string;
  allergenDeclared: string;
  mayContain: string;
  producedInFacility: string;
  allergenDeclaredNotes: string;
  mayContainNotes: string;
  producedInFacilityNotes: string;
  directionForUse: string;
  warnings: string;
  disclaimer: string;
  productClaimText: string;
  productBenefitText: string;
  ingredientClaimText: string;
}

export interface SupBulkPreviewRow {
  rowNumber: number;
  valid: boolean;
  configured: boolean;
  error?: string;
  cells: Record<string, string>;
  display: Record<string, string>;
  parsed: SupBulkParsedText;
  selections: SupBulkRowSelections;
  payload?: Record<string, unknown>;
}

export interface SupBulkParseResult {
  rows: SupBulkPreviewRow[];
  validCount: number;
  errorCount: number;
  totalCount: number;
  fileError?: string;
}

export interface SupBulkLookups {
  dietaryPreferences: SupBulkLookupOption[];
  dataSources: SupBulkLookupOption[];
  databaseSources: SupBulkLookupOption[];
  countries: SupBulkLookupOption[];
  tastes: SupBulkLookupOption[];
  flavours: SupBulkLookupOption[];
  allergens: SupBulkLookupOption[];
  allergenFree: SupBulkLookupOption[];
  intolerances: SupBulkLookupOption[];
  intoleranceFree: SupBulkLookupOption[];
  fssaiCategories: SupBulkLookupOption[];
  eStoreCategories: SupBulkLookupOption[];
  units: SupBulkLookupOption[];
  dosageUnits: SupBulkLookupOption[];
  supplementForms: SupBulkLookupOption[];
  nutrients: SupBulkLookupOption[];
  nutrientGroups: SupBulkLookupOption[];
  nutrientTags: SupBulkNutrientTagOption[];
  claimTypes: SupBulkLookupOption[];
  claimTags: SupBulkClaimTagOption[];
  productBenefits: SupBulkLookupOption[];
  productBenefitTags: SupBulkClaimTagOption[];
  certificateOptions: SupBulkLookupOption[];
  licenseOptions: SupBulkLookupOption[];
}

export const SUP_BULK_MAX_ROWS = 5000; // align with recipe/finish-product bulk-create cap

export const SUP_BULK_STATUS_OPTIONS = [
  'Draft',
  'To be assigned',
  'Assigned to Maker',
  'Save as Draft',
  'First review',
  'Assigned to Checker',
  'Corrections Needed',
  'Corrections Done',
  'Checked',
  'Published',
];

export const SUP_BULK_SHELF_LIFE_UNITS = ['Days', 'Months', 'Years', 'Hours', 'Minutes'];
export const SUP_BULK_NET_QTY_UNITS = ['g', 'kg', 'ml', 'L', 'mg', 'mcg'];

export const SUP_BULK_TEMPLATE_HEADERS = [
  'CompanyName',
  'Address',
  'ExecutiveOwner',
  'MobileNumber',
  'Email',
  'GstStatus',
  'Country',
  'State',
  'City',
  'Pincode',
  'BrandName',
  'ProductName',
  'DietaryPreference',
  'ProductForm',
  'DataSource',
  'DatabaseSource',
  'CountryOfOrigin',
  'BrandWebsite',
  'Instagram',
  'Facebook',
  'LinkedIn',
  'ShelfLife',
  'ShelfLifeUnit',
  'NetQuantity',
  'NetQuantityUnit',
  'LotNumber',
  'ReferenceUrl',
  'AvailableFlavour',
  'SupplementDescription',
  'DataCapturingDate',
  'DateOfManufacturing',
  'DateOfExpiry',
  'BestBeforeDate',
  'FssaiCategory',
  'EStoreCategory',
  'ManufacturedByName',
  'ManufacturedByAddress',
  'ManufacturedByLicenseNo',
  'ManufacturedByCountry',
  'ManufacturedByState',
  'ManufacturedByCity',
  'ManufacturedByPincode',
  'MarketedByName',
  'MarketedByAddress',
  'MarketedByLicenseNo',
  'MarketedByCountry',
  'MarketedByState',
  'MarketedByCity',
  'MarketedByPincode',
  'PackagedByName',
  'PackagedByAddress',
  'PackagedByLicenseNo',
  'PackagedByCountry',
  'PackagedByState',
  'PackagedByCity',
  'PackagedByPincode',
  'SKUDetails',
  'Ingredients',
  'RecommendedDosageAmount',
  'RecommendedDosageUnit',
  'RecommendedDosageNote',
  'Tastes',
  'Flavours',
  'Macros',
  'Micros',
  'AllergenDeclared',
  'MayContain',
  'ProducedInFacility',
  'AllergenDeclaredNotes',
  'MayContainNotes',
  'ProducedInFacilityNotes',
  'ProductClaimText',
  'ProductBenefitText',
  'IngredientClaimText',
  'DirectionForUse',
  'Warnings',
  'Disclaimer',
];

export const SUP_BULK_TEMPLATE_SAMPLE_ROW: Record<string, string> = {
  CompanyName: 'Vital Nutrition Pvt Ltd',
  Address: 'Plot 8, Pharma Zone, Ahmedabad',
  ExecutiveOwner: 'Priya Shah',
  MobileNumber: '9876501234',
  Email: 'info@vitalnutrition.com',
  GstStatus: 'Registered',
  Country: 'India',
  State: 'Gujarat',
  City: 'Ahmedabad',
  Pincode: '380015',
  BrandName: 'VitalPlus',
  ProductName: 'Vitamin D3 Softgels',
  DietaryPreference: 'Veg',
  ProductForm: 'Capsule',
  DataSource: 'FSSAI',
  DatabaseSource: 'Manufacturer Label',
  CountryOfOrigin: 'India',
  BrandWebsite: 'https://www.vitalnutrition.com',
  Instagram: '@vitalplus',
  Facebook: 'VitalPlus',
  LinkedIn: 'Vital Nutrition',
  ShelfLife: '24',
  ShelfLifeUnit: 'Months',
  NetQuantity: '60',
  NetQuantityUnit: 'g',
  LotNumber: 'SUP-2024-001',
  ReferenceUrl: '',
  AvailableFlavour: 'Unflavoured',
  SupplementDescription: 'Vitamin D3 supplement for daily wellness',
  DataCapturingDate: '',
  DateOfManufacturing: '',
  DateOfExpiry: '',
  BestBeforeDate: '',
  FssaiCategory: 'FSSAI One',
  EStoreCategory: 'zomato',
  ManufacturedByName: 'Vital Manufacturing',
  ManufacturedByAddress: 'Industrial Estate',
  ManufacturedByLicenseNo: 'LIC-MFG-101',
  ManufacturedByCountry: 'India',
  ManufacturedByState: 'Gujarat',
  ManufacturedByCity: 'Ahmedabad',
  ManufacturedByPincode: '380015',
  MarketedByName: 'Vital Marketing',
  MarketedByAddress: 'SG Highway',
  MarketedByLicenseNo: 'LIC-MKT-101',
  MarketedByCountry: 'India',
  MarketedByState: 'Gujarat',
  MarketedByCity: 'Ahmedabad',
  MarketedByPincode: '380015',
  PackagedByName: 'Vital Packaging',
  PackagedByAddress: 'Packaging Hub',
  PackagedByLicenseNo: 'LIC-PKG-101',
  PackagedByCountry: 'India',
  PackagedByState: 'Gujarat',
  PackagedByCity: 'Ahmedabad',
  PackagedByPincode: '380015',
  SKUDetails: '60 Caps|60|499|||',
  Ingredients: 'Vitamin D3|1000|IU;;MCT Oil|100|mg',
  RecommendedDosageAmount: '1',
  RecommendedDosageUnit: 'Capsule',
  RecommendedDosageNote: 'Once daily after meal',
  Tastes: 'Sweet',
  Flavours: 'Bland',
  Macros: 'Energy / Calories|KCAL|5||;;Protein|g|0||;;Total Fat|g|0.5||',
  Micros: 'Vitamin D|IU|1000|50|',
  AllergenDeclared: 'Dairy',
  MayContain: 'Soya',
  ProducedInFacility: 'milk',
  AllergenDeclaredNotes: 'Contains milk derivatives',
  MayContainNotes: 'May contain soy',
  ProducedInFacilityNotes: '',
  ProductClaimText: 'Supports bone health',
  ProductBenefitText: 'Daily vitamin D support',
  IngredientClaimText: '',
  DirectionForUse: 'Take one softgel daily',
  Warnings: 'Keep out of reach of children',
  Disclaimer: 'For bulk upload testing',
};

export function createEmptyEntitySelections(): SupBulkEntitySelections {
  return {
    countryId: null,
    stateId: null,
    cityId: null,
    pincodeId: null,
    countryName: null,
    stateName: null,
    cityName: null,
    pincode: null,
    licenseImage: null,
  };
}

export interface SupBulkFieldError {
  field: string;
  message: string;
  section: number;
}

export function createDefaultSupBulkSelections(): SupBulkRowSelections {
  return {
    status: 'Draft',
    countryId: null,
    stateId: null,
    cityId: null,
    pincodeId: null,
    dietaryPreferenceId: null,
    productForm: null,
    dataSourceId: null,
    databaseSourceId: null,
    countryOfOriginId: null,
    shelfLifeUnit: null,
    netQuantityUnit: null,
    frontImage: null,
    backImage: null,
    leftImage: null,
    rightImage: null,
    frontImageUploading: false,
    authorizedCertificateImage: null,
    authorizedCertificateUploading: false,
    authorizedLicenseImage: null,
    authorizedLicenseUploading: false,
    manufacturedBy: createEmptyEntitySelections(),
    marketedBy: createEmptyEntitySelections(),
    packagedBy: createEmptyEntitySelections(),
    tasteIds: [],
    flavourIds: [],
    ingredientDrafts: [],
    ingredients: [],
    recommendedDosageAmount: null,
    recommendedDosageUnitId: null,
    recommendedDosageNote: null,
    macronutrients: [],
    micronutrients: [],
    allergenDeclaredIds: [],
    mayContainIds: [],
    producedInFacilityIds: [],
    allergenContainsIds: [],
    allergenFreeIds: [],
    intoleranceContainsIds: [],
    intoleranceFreeIds: [],
    productClaims: [],
    productBenefitClaims: [],
    ingredientClaims: [],
    healthConditionTags: [],
    generatedFssaiNutrientTags: [],
    skuDetails: [],
    assignment: null,
  };
}

export const SUPPLEMENT_BULK_MEDIA_FOLDERS = {
  packaging: 'supplement-packaging',
  license: 'supplement-licenses',
  certificate: 'supplement-certificates',
  sku: 'supplement-sku',
} as const;
